import { computed } from "vue";
import { useStore } from "vuex";
import { renderEntity } from "@/src/utils/utils.js";
import { chatTypes, chatRecipientTypes } from "@/src/constants/chatConstants.js";

export function useDMResolution() {
  const store = useStore();
  const allUsers = computed(() => store.getters["users/users"] || []);
  const currentUserId = computed(() => store.getters["auth/userData"]?.id);

  /**
   * For 1:1 DMs where chat.name matches the current user (i.e. the backend named
   * the group from the creator's perspective), resolve the other participant's name.
   * Returns null if no resolution is needed (name is already correct).
   */
  const resolveOtherDMUserName = (chat) => {
    if (!chat?.is_dm || chat?.recipient_type !== chatRecipientTypes.ONE_ON_ONE) return null;
    if (chat?.type === chatTypes.SELF_DM) return null;
    if (chat?.type !== chatTypes.DM) return null;

    const currentUser = allUsers.value.find(u => Number(u.id) === Number(currentUserId.value));
    if (!currentUser || !chat.name) return null;
    if (renderEntity(currentUser).toLowerCase().trim() !== chat.name.toLowerCase().trim()) return null;

    // chat.name is our own name — find the other participant
    if (chat.created_by && Number(chat.created_by) !== Number(currentUserId.value)) {
      const creator = allUsers.value.find(u => Number(u.id) === Number(chat.created_by));
      if (creator) return renderEntity(creator);
    }
    if (chat.members?.length > 0) {
      const otherMember = chat.members.find(m => Number(m.user_id || m.user?.id) !== Number(currentUserId.value));
      if (otherMember) {
        const otherUser = allUsers.value.find(u => Number(u.id) === Number(otherMember.user_id || otherMember.user?.id));
        if (otherUser) return renderEntity(otherUser);
      }
    }
    return null;
  };

  /**
   * For 1:1 DMs where chat.name matches the current user, resolve the other
   * participant's user object (for profile icon / avatar).
   * Returns null if no resolution is needed.
   */
  const resolveOtherDMUser = (chat) => {
    if (!chat?.is_dm || chat?.recipient_type !== chatRecipientTypes.ONE_ON_ONE) return null;
    if (chat?.type === chatTypes.SELF_DM) return null;
    if (chat?.type !== chatTypes.DM) return null;

    const currentUser = allUsers.value.find(u => Number(u.id) === Number(currentUserId.value));
    if (!currentUser || !chat.name) return null;
    if (renderEntity(currentUser).toLowerCase().trim() !== chat.name.toLowerCase().trim()) return null;

    if (chat.created_by && Number(chat.created_by) !== Number(currentUserId.value)) {
      const creator = allUsers.value.find(u => Number(u.id) === Number(chat.created_by));
      if (creator) return creator;
    }
    if (chat.members?.length > 0) {
      const otherMember = chat.members.find(m => Number(m.user_id || m.user?.id) !== Number(currentUserId.value));
      if (otherMember) {
        const otherUser = allUsers.value.find(u => Number(u.id) === Number(otherMember.user_id || otherMember.user?.id));
        if (otherUser) return otherUser;
      }
    }
    return null;
  };

  return {
    resolveOtherDMUserName,
    resolveOtherDMUser,
  };
}
